import usePassportSchema from '@/pages/MDM/hooks/usePassprotSchema'
import usePrimitives from '@/pages/MDM/hooks/usePrimitives'
import useUnitInstances from '@/pages/MDM/hooks/useUnitInstances'
import { IPassportGroup, IPassportSchema, IPassportSchemaField } from '@/pages/MDM/mdm.types'
import mdmUtils from '@/pages/MDM/mdm.utils'
import objectUtils from '@/utils/object.utils'
import { CSSProperties, forwardRef, useMemo, useRef } from 'react'
import ArrowRight from '@/assets/icons/arrowRightLine.svg?react'
import BasicTable from '@/shared/Tables/BasicTable'
import css from './equipmentPassportСomparisonView.module.css'
import useActiveObservableElement from '@/shared/hooks/useActiveObservableElement'
import { TEquipmentPassportDifferences } from '../../SidePages/EditEquipmentPassportSidePage/hooks/usePassportDifferences'

interface IProps {
  passportDifferences: TEquipmentPassportDifferences
  changedGroups: IPassportGroup[]
  passportSchemaId: IPassportSchema['id']
  onGroupIntoViewportChange?: (activeGroupId: IPassportGroup['id']) => void
  isChangedOnly?: boolean
}

const EquipmentPassportСomparisonView = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const observingElements = useRef<{ element: HTMLElement; key: number }[]>([])
  const passportSchema = usePassportSchema(props.passportSchemaId)
  const unitInstances = useUnitInstances()
  const primitives = usePrimitives()

  const groupedFields: Array<{ id: IPassportGroup['id']; groupName: string; childrens: IPassportSchemaField[] }> =
    useMemo(() => {
      if (!passportSchema) return []

      const filteredPassportSchemaFields = props.isChangedOnly
        ? passportSchema.fields.filter((field) => props.passportDifferences[field.id]?.isChanged)
        : passportSchema.fields

      const fieldsByGroup = objectUtils.arrToKeyArray(filteredPassportSchemaFields, 'groupId')

      const changedGroupsSet = new Set(props.changedGroups.map((g) => g.id))
      const groups = props.isChangedOnly
        ? passportSchema.groups.filter((g) => changedGroupsSet.has(g.id))
        : passportSchema.groups

      const result: Array<{ id: IPassportGroup['id']; groupName: string; childrens: IPassportSchemaField[] }> = []

      for (const group of groups) {
        if (fieldsByGroup[group.id]?.length) {
          result.push({
            id: group.id,
            groupName: group.name,
            childrens: [...fieldsByGroup[group.id]].sort((a, b) => a.orderingIndex - b.orderingIndex),
          })
        }
      }
      return result
    }, [passportSchema, props.changedGroups, props.isChangedOnly])

  useActiveObservableElement({
    elements: observingElements.current,
    onCurrentObservingElementChange: props.onGroupIntoViewportChange,
    active: Boolean(props.onGroupIntoViewportChange),
  })

  const getFieldValues = (field: IPassportSchemaField) => {
    const passportDifference = props.passportDifferences[field.id]

    if ((props.isChangedOnly && !passportDifference.isChanged) || !passportDifference) return {}

    const prevFieldValue = mdmUtils.getFieldValue(primitives, unitInstances, passportDifference.left) || '-'
    const fieldValue = mdmUtils.getFieldValue(primitives, unitInstances, passportDifference.right) || '-'

    return { prevFieldValue, fieldValue }
  }

  const tableValues = (childrens: IPassportSchemaField[]) => {
    const res = []
    for (let field of childrens) {
      const { prevFieldValue, fieldValue } = getFieldValues(field)
      if (!prevFieldValue || !fieldValue) continue
      res.push([
        <span className="text-nm" style={{ whiteSpace: 'pre-wrap' }}>
          {field.name}
        </span>,
        <span className="text-nm" style={{ whiteSpace: 'pre-wrap' }}>
          {prevFieldValue}
        </span>,
        <div className="flex-lines height100 width100 center">
          <ArrowRight className={css.arrowIcon} />
        </div>,
        <span className="text-nm" style={{ whiteSpace: 'pre-wrap' }}>
          {fieldValue}
        </span>,
      ])
    }

    return res
  }

  const getCellStyles = (rowIndex: number, colIndex: number, childrens: IPassportSchemaField[]): CSSProperties => {
    const res: CSSProperties = {}

    if (rowIndex === 0 && colIndex !== 2) res.borderTop = '1px solid var(--border-secondary-color)'

    if (colIndex !== 2) {
      res.borderBottom = '1px solid var(--border-secondary-color)'
      const passportSchemaField = childrens[rowIndex]
      const passportDifference = props.passportDifferences[passportSchemaField.id]
      switch (true) {
        case colIndex === 1 && passportDifference?.isChanged:
          res.backgroundColor = 'var(--error-bg)'
          return res
        case colIndex === 3 && passportDifference?.isChanged:
          res.backgroundColor = 'var(--succes-bg)'
          return res
        default:
          return res
      }
    }
  }

  const renderContent = () => {
    return groupedFields.map((f, index) => (
      <div
        className="flex-lines gap16"
        key={f.id}
        id={`groupId_${f.id}`}
        ref={(el) => (observingElements.current[index] = { element: el, key: f.id })}
      >
        <h5 className="text-heading-5">{f.groupName}</h5>
        <BasicTable
          colCount={4}
          values={tableValues(f.childrens)}
          wrapperStyles={{ borderTop: 'none', borderBottom: 'none' }}
          rowStyles={{ gridTemplateColumns: '1fr 1fr 40px 1fr', borderBottom: 'none' }}
          getCellStyle={(rowIndex, colIndex) => getCellStyles(rowIndex, colIndex, f.childrens)}
        />
      </div>
    ))
  }

  return (
    <div ref={ref} className="flex-lines gap16 width100">
      {renderContent()}
    </div>
  )
})

export default EquipmentPassportСomparisonView
